import { combineMutiple, getMaxBetOdds, getMaxCombineOdds, getMinCombineOdds, getMaxCombineBonus } from './cr'

/**
 * 获取串关名称
 * @param  {Number} count selection 数量
 * @return {String}       串关名
 */
export const getMultipleName = (count) => {
  if (count === 1) {
    return 'Singles'
  } else if (count === 2) {
    return 'Doubles'
  } else if (count === 3) {
    return 'Trebles'
  } else {
    return `${count} Folds`
  }
}

/**
 * 获取 selectionList 中相同 event 对应 selection 数量对象，结果为 { [eventId]: [count] }
 * @param  {Array}  eventIds   event id list
 * @return {Object}            { [eventId]: [count] }
 */
export const getEventCountMap = (eventIds = []) => {
  const tmp = {}
  eventIds.forEach((id) => {
    if (tmp[id]) {
      tmp[id] = tmp[id] + 1
    } else {
      tmp[id] = 1
    }
  })
  return tmp
}

/**
 * 获取 bet count 组合数
 * @param  {Array}  selectionList  selections list
 * @param  {Number} type           选取几个进行组合，相同 event 会互斥
 * @return {Number}                bet count
 */
export const getBetCount = (selectionList = [], type = 1) => {
  const eventIds = selectionList.map((x) => x.eventId)
  const eventCountMap = getEventCountMap(eventIds)

  const list = Object.values(eventCountMap)
  return parseInt(combineMutiple(list, type))
}

/**
 * 获取 total odds
 * @param  {Array} selectionList  选项集合
 * @return {Number}               total odds
 */
export const getTotalOdds = (selectionList = []) => {
  return selectionList.reduce((sub, cur) => (sub * 100 * (+cur.outcomeInfo.odds * 100)) / 10000, 1)
}

const getEventSelectionMap = (selectionList = []) => {
  const tmp = {}
  selectionList.forEach((selection) => {
    const { eventId } = selection
    if (tmp[eventId]) {
      tmp[eventId].push(selection)
    } else {
      tmp[eventId] = [selection]
    }
  })
  return tmp
}

/**
 * 获取 event odds list, 同一个 event 会聚合在一起
 * 返回值，如 ['1.2', '2.3', ['2.1', '3.2']]
 */
const getEventOddsList = (selectionList = []) => {
  const selectionMap = getEventSelectionMap(selectionList)

  const oddsList = Object.entries(selectionMap).map(([, list]) => {
    if (list.length === 1) {
      return list[0].outcomeInfo.odds
    } else {
      return list.map((x) => x.outcomeInfo.odds)
    }
  })

  return oddsList
}

// 获取 total odds 最小值和最大值
export const getTotalOddsRange = (selectionList = [], betType = 1, maxLimitOdds) => {
  const oddsList = getEventOddsList(selectionList)
  const min = getMinCombineOdds(oddsList, betType, maxLimitOdds)
  const max = getMaxCombineOdds(oddsList, betType, maxLimitOdds)
  // 保留 odds 对应数量的小数位
  // const decimals = betType * 2
  // return [+(min.toFixed(decimals)), +(max.toFixed(decimals))]
  return [min, max]
}

// 获取单注最大 odds
export const getMaxOddsForBets = (selectionList = [], betType = 1) => {
  const oddsList = getEventOddsList(selectionList)
  return getMaxBetOdds(oddsList, betType)
}

/**
 * 获取重复 eventId 集合
 * @param  {Array}  eventIds 源 event ids
 * @return {Array}           重复 event id 集合
 */
export const getDupliEventId = (eventIds = []) => {
  const tmp = {}
  const arr = []
  eventIds.forEach((id) => {
    if (tmp[id]) {
      arr.push(id)
    } else {
      tmp[id] = true
    }
  })
  return [...new Set(arr)]
}

/**
 * 获取 bonus 最大最小值
 * @param  {Array}  selectionList [除 banker 外剩余 selection 列表]
 * @param  {Number} betType       [可以再选几个]
 * @return {[type]}               [description]
 */
export const getBonusInfo = (selectionList = [], betType = 1, { bankerOddsList = [], maxLimitBonus, qualifyingOddsLimit = 1.2, bonusRatios = {}, stake, maxLimitOdds } = {}) => {
  const oddsList = getEventOddsList(selectionList)
  // min
  const minList = oddsList
    .map((x) => {
      if (Array.isArray(x)) {
        return Math.min(...x)
      } else {
        return +x
      }
    })
    .sort((a, b) => a - b)
    .slice(0, betType)

  const validBonusBankerOddsList = bankerOddsList.filter((x) => +x >= qualifyingOddsLimit)
  const validBonusBankerOddsCount = validBonusBankerOddsList.length

  const bankerOdds = bankerOddsList.reduce((total, odds) => total * odds, 1)
  const minBonusKey = minList.filter((x) => +x >= qualifyingOddsLimit).length + validBonusBankerOddsCount

  let minBonus = 0
  if (minBonusKey) {
    const ratio = (bonusRatios[minBonusKey] || 0) / 100
    if (ratio > 0) {
      const minOdds = getMinCombineOdds(oddsList, betType, maxLimitOdds)
      minBonus = minOdds * ratio * stake * bankerOdds
      minBonus = minBonus > maxLimitBonus ? maxLimitBonus : minBonus
    }
  }

  // 全部都选作 banker, 或者 banker 选项数量达到最大值
  if (betType === 0) {
    return [minBonus, minBonus]
  }

  const maxBonus = getMaxCombineBonus(oddsList, betType, {
    bankerOdds,
    validBonusBankerOddsCount,
    maxLimitBonus,
    qualifyingOddsLimit,
    bonusRatios,
    stake
  })

  if (minBonus.toFixed(2) === maxBonus.toFixed(2)) {
    const bonus = parseFloat(minBonus.toFixed(2))
    return [bonus, bonus]
  }

  return [minBonus, maxBonus]
}
